"use client";

import * as React from "react";
import { X } from "lucide-react";
import { cn } from "@repo/utils";

interface TagsInputProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange"> {
  value: string[];
  onValueChange: (value: string[]) => void;
  placeholder?: string;
  maxItems?: number;
  minItems?: number;
  disabled?: boolean;
}

const TagsInput = React.forwardRef<HTMLDivElement, TagsInputProps>(
  (
    {
      value = [],
      onValueChange,
      placeholder = "Add tag...",
      maxItems,
      minItems = 0,
      disabled = false,
      className,
      ...props
    },
    ref
  ) => {
    const [inputValue, setInputValue] = React.useState("");
    const [activeIndex, setActiveIndex] = React.useState<number | null>(null);
    const inputRef = React.useRef<HTMLInputElement>(null);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      const target = e.target as HTMLInputElement;

      if (e.key === "Enter" || e.key === ",") {
        e.preventDefault();
        const trimmedValue = inputValue.trim();
        if (trimmedValue && !value.includes(trimmedValue)) {
          if (!maxItems || value.length < maxItems) {
            onValueChange([...value, trimmedValue]);
            setInputValue("");
          }
        }
      } else if (e.key === "Backspace" && !target.value && value.length > 0) {
        if (activeIndex !== null) {
          removeTag(activeIndex);
          setActiveIndex(null);
        } else {
          setActiveIndex(value.length - 1);
        }
      } else if (e.key === "ArrowLeft" && !target.value) {
        if (activeIndex === null && value.length > 0) {
          setActiveIndex(value.length - 1);
        } else if (activeIndex !== null && activeIndex > 0) {
          setActiveIndex(activeIndex - 1);
        }
      } else if (e.key === "ArrowRight" && activeIndex !== null) {
        if (activeIndex < value.length - 1) {
          setActiveIndex(activeIndex + 1);
        } else {
          setActiveIndex(null);
          inputRef.current?.focus();
        }
      } else if (e.key === "Delete" && activeIndex !== null) {
        removeTag(activeIndex);
        setActiveIndex(null);
      } else if (e.key === "Escape") {
        setActiveIndex(null);
        inputRef.current?.focus();
      } else {
        setActiveIndex(null);
      }
    };

    const removeTag = (index: number) => {
      if (value.length <= minItems) return;
      const newValue = [...value];
      newValue.splice(index, 1);
      onValueChange(newValue);
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      const pastedText = e.clipboardData.getData("text");
      const tags = pastedText
        .split(/[,\n\t]/)
        .map((tag) => tag.trim())
        .filter((tag) => tag && !value.includes(tag));

      if (tags.length > 0) {
        const maxToAdd = maxItems ? maxItems - value.length : tags.length;
        const tagsToAdd = tags.slice(0, maxToAdd);
        onValueChange([...value, ...tagsToAdd]);
      }
    };

    return (
      <div
        ref={ref}
        className={cn(
          "flex min-h-10 w-full flex-wrap items-center gap-1.5 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
          disabled && "cursor-not-allowed opacity-50",
          className
        )}
        onClick={() => inputRef.current?.focus()}
        {...props}
      >
        {value.map((tag, index) => (
          <span
            key={`${tag}-${index}`}
            className={cn(
              "inline-flex items-center gap-1 rounded-md bg-secondary px-2 py-0.5 text-sm font-medium text-secondary-foreground",
              activeIndex === index && "ring-2 ring-ring ring-offset-2",
              disabled && "opacity-50"
            )}
          >
            {tag}
            {!disabled && value.length > minItems && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeTag(index);
                }}
                className="rounded-sm hover:bg-secondary-foreground/20"
              >
                <X className="size-3" />
              </button>
            )}
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          placeholder={value.length === 0 ? placeholder : ""}
          disabled={disabled || (maxItems !== undefined && value.length >= maxItems)}
          className="flex-1 bg-transparent outline-none placeholder:text-muted-foreground min-w-[80px]"
        />
      </div>
    );
  }
);

TagsInput.displayName = "TagsInput";

export { TagsInput };
